import { useEffect, useState } from 'react';

type ShareModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
};

const channels = [
  { label: '微信', icon: 'ri-wechat-fill', hint: '请在微信中粘贴链接分享' },
  { label: '微博', icon: 'ri-weibo-fill', hint: '演示环境不跳转微博' },
  { label: 'QQ', icon: 'ri-qq-fill', hint: '演示环境不跳转 QQ' },
];

export default function ShareModal({ open, onClose, title }: ShareModalProps) {
  const [hint, setHint] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setHint('');
      setCopied(false);
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setHint('链接已复制，可以粘贴到聊天窗口');
    } catch {
      setHint('当前浏览器无法自动复制，请手动复制地址栏链接');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="关闭分享"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-foreground-950/60 animate-fade-in"
      />
      <div className="relative z-10 w-full rounded-t-xl border border-background-200 bg-background-50 p-6 animate-pop-in sm:max-w-md sm:rounded-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg text-foreground-900">分享这篇内容</h3>
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">{title}</p>
          </div>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500 transition-colors hover:text-primary-600"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {channels.map((channel) => (
            <button
              key={channel.label}
              type="button"
              onClick={() => setHint(channel.hint)}
              className="flex flex-col items-center gap-2 rounded-lg border border-background-200 bg-background-100/60 px-2 py-4 transition-colors hover:border-primary-300 hover:bg-primary-50"
            >
              <span className="flex h-9 w-9 items-center justify-center text-2xl text-primary-600">
                <i className={channel.icon} />
              </span>
              <span className="text-xs text-foreground-700">{channel.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-3 py-2">
          <span className="flex-1 truncate text-xs text-foreground-500">{window.location.href}</span>
          <button
            type="button"
            onClick={copyLink}
            className="whitespace-nowrap rounded-md bg-primary-600 px-3 py-1.5 text-xs text-background-50 transition-colors hover:bg-primary-700"
          >
            {copied ? '已复制' : '复制链接'}
          </button>
        </div>

        {hint && <p className="mt-3 text-center text-xs text-accent-700">{hint}</p>}
        <p className="mt-4 text-center text-[11px] text-foreground-400">
          演示站点：分享按钮仅展示交互结构，不调用真实第三方分享接口
        </p>
      </div>
    </div>
  );
}